import Button from "@/Components/Button/Button";
import Modal from "@/Components/Modal/Modal";
import RichTextContent from "@/Components/RichTextEditor/RichTextContent";
import RteIcon from "@/Components/RichTextEditor/RteIcon";
import { formatDate } from "@/shared/utils/helpers";
import { SystemMessageBody } from "@/shared/utils/interfaces";
import { useTranslation } from "react-i18next";

interface SystemMessageModalProps {
  message: Pick<SystemMessageBody, "title" | "content" | "start_date">;
  // Called by the button and by closing the modal
  onDone: () => void;
  // Position in a queue of messages, 1 based
  position?: number;
  total?: number;
  // Admin preview, polls can't be voted on
  preview?: boolean;
}

const SystemMessageModal = ({
  message,
  onDone,
  position = 1,
  total = 1,
  preview = false,
}: SystemMessageModalProps) => {
  const { t } = useTranslation("", { keyPrefix: "systemMessages.modal" });
  const hasNext = position < total;

  return (
    <Modal
      onExit={onDone}
      closeOnClickOutside={false}
      className="md:w-200 lg:w-230"
      maxHeight="90%"
      contentClassName="p-0 md:pt-0 lg:pt-0 pt-0"
    >
      <div className="border-b border-border bg-primary/5 px-8 pb-8 pt-10 md:px-12 md:pt-12">
        <div className="mb-4 flex flex-wrap items-center gap-3 pr-14">
          <span className="flex h-[4rem] w-[4rem] items-center justify-center rounded-full bg-primary text-white">
            <RteIcon name="megaphone" className="h-[2rem] w-[2rem]" />
          </span>
          <span className="text-small font-semibold uppercase tracking-wide text-primary">
            {preview ? t("preview") : t("from")}
          </span>
          <span className="text-small text-text-tertiary">
            · {formatDate(message.start_date, "longDate")}
          </span>
        </div>
        <h2 className="m-0 text-[2.6rem] font-bold leading-tight text-text">
          {message.title}
        </h2>
      </div>

      <div className="px-8 py-8 md:px-12">
        <RichTextContent html={message.content} previewPolls={preview} />
      </div>

      <div className="flex items-center justify-between gap-4 border-t border-border px-8 py-6 md:px-12">
        <span className="text-small text-text-tertiary">
          {total > 1 && t("position", { position, total })}
        </span>
        <Button type="button" onClick={onDone}>
          {hasNext ? t("next") : t("gotIt")}
        </Button>
      </div>
    </Modal>
  );
};

export default SystemMessageModal;
